import { makeAutoObservable } from 'mobx';
import { v4 as UUID } from 'uuid';
import { HttpBackend } from '../../Services/Http/HttpBackend';
import { EquipmentType } from '../Equipment/EquipmentType';

export type UserLocationType = 'facility' | 'home' | 'outdoor';

// Raw JSON from backend
export type AthleteEquipmentJson = {
  id: string;
  athleteId?: string;
  name?: string;
  locationType: UserLocationType;
  equipmentTypes: string[];
  excludedEquipmentTypes: string[];
};

// Observable model type
export type AthleteEquipment = AthleteEquipmentJson & {
  readonly isFree: boolean;
  readonly emptyOrFree: boolean;
  readonly nonFreeEquipmentTypes: string[];
  toggleSelection(type: EquipmentType): void;
  save(): Promise<AthleteEquipment>;
  delete(): Promise<void>;
};

// Factory function
export function createAthleteEquipment(json?: Partial<AthleteEquipmentJson>): AthleteEquipment {
  return makeAutoObservable({
    id: json?.id || UUID(),
    athleteId: json?.athleteId,
    name: json?.name,
    locationType: json?.locationType || 'facility',
    equipmentTypes: json?.equipmentTypes || [],
    excludedEquipmentTypes: json?.excludedEquipmentTypes || [],

    get isFree(): boolean {
      const hasFree = this.equipmentTypes.includes('FREE');
      const hasFreeOutdoor = this.equipmentTypes.includes('FREE_OUTDOOR');
      return (
        (this.equipmentTypes.length === 1 && (hasFree || hasFreeOutdoor)) ||
        (this.equipmentTypes.length === 2 && hasFree && hasFreeOutdoor)
      );
    },

    get emptyOrFree(): boolean {
      if (this.equipmentTypes.length > 0) {
        return this.isFree;
      }
      return true;
    },

    get nonFreeEquipmentTypes(): string[] {
      return this.equipmentTypes.filter((et) => et !== 'FREE' && et !== 'FREE_OUTDOOR');
    },

    toggleSelection(type: EquipmentType) {
      const index = this.equipmentTypes.findIndex((t) => t === type.id);
      if (index === -1) {
        this.equipmentTypes.push(type.id);
      } else {
        this.equipmentTypes.splice(index, 1);
      }
    },

    save(): Promise<AthleteEquipment> {
      return HttpBackend.post('/athlete/equipment', this).then(() => this);
    },

    delete() {
      return HttpBackend.delete(`/athlete/equipment/${this.id}`);
    },
  });
}

// Static API methods
export const AthleteEquipmentApi = {
  find(): Promise<AthleteEquipment[]> {
    return HttpBackend.get('/athlete/equipment').then((res: AthleteEquipmentJson[]) => 
      (res || []).map((e) => createAthleteEquipment(e))
    );
  },

  current(): Promise<AthleteEquipment | undefined> {
    return HttpBackend.get('/athlete/equipment/current').then((result) =>
      result ? createAthleteEquipment(result) : undefined,
    );
  },

  list(): Promise<AthleteEquipment[]> {
    return HttpBackend.get('/athlete/equipment/list').then((res: AthleteEquipmentJson[]) =>
      (res || []).map((e) => createAthleteEquipment(e)),
    );
  },
};
